import _ from "lodash";
import React, { useMemo } from "react";

import { DateTime } from "luxon";
import { Checkbox, Grid, Loader, Popup, Segment, Table } from "semantic-ui-react";
import type { AucTableData, PerFlightValues, FlightStats } from "../../../../apis/types/analysisTypes";
import type { Treatment, TrialData } from "../../../../apis/types/trialTypes";
import { buildColorMatrix, calculateShadingForNumbers } from "../../../Lumber/TableShader";
import { useTrialAnalysis, useTrialAnalysisDispatch } from "../../Trials/TrialAnalysis/TrialAnalysisContext";
import "./styles.css";
import { getDecimalsByGate } from "../../../../utilities/fmt";

type Props = {
	aucData?: AucTableData[];
	statsData?: FlightStats[];
	flights?: { flightId: string; flightDate: string; isPlantFlight: boolean; isHarvestFlight: boolean }[]; // assumed this comes in sorted
	gduList?: { day: string; gdu: number; monthDay: string }[];
	analysisName: string;
	timecoursePValue?: string;
	timecourseMeanComparisons?: { flightId: string; treatments: { trialTreatmentId: number; letter: string }[] }[];
	timecourseAnovaTableRows?: { term: string; numDF: number; denDF: number; fValue: number; pValue: number }[];
	aucMeanComparisons?: {
		cv: string;
		mean: string;
		pValue: string;
		letteringPairs: { treatmentId: number; letter: string }[];
	};
	loadingStats: boolean;
	loadingTable: boolean;
	trialData?: TrialData;
};

const AreaUnderTheCurveTableNew = ({
	aucData,
	statsData,
	flights,
	gduList = [],
	analysisName,
	timecoursePValue,
	timecourseMeanComparisons,
	timecourseAnovaTableRows,
	aucMeanComparisons,
	loadingStats,
	loadingTable,
	trialData,
}: Props) => {
	const DATE_FORMAT = "L/d/yyyy";
	const weatherDataEnabled = true;

	const { pending, committed, isInitialized, isAucOutdated, isLettersOutdated, display } = useTrialAnalysis();
	const { changeSettings } = useTrialAnalysisDispatch();

	//-- Data
	const [tableBodyData, tableAucData, transposedBodyData]: [
		PerFlightValues[],
		{ auc: number; stdDev: number }[],
		Record<string, number[]>,
	] = useMemo(() => {
		const body = _.reduce<AucTableData, PerFlightValues[]>(
			aucData,
			(acc, curr) => {
				acc[curr.trialTreatmentId] = curr.perFlightValues;
				return acc;
			},
			[],
		);

		const transposed = _.chain(body)
			.reduce<Record<string, number[]>>((acc, curr, index) => {
				_.forEach(curr, (value, key) => {
					// get the key for accumulator, if undefined then init to empty array and set index to value
					(acc[key] ??= [])[index] = value;
				});
				return acc;
			}, {})
			.value();

		const auc = _.reduce<AucTableData, { auc: number; stdDev: number }[]>(
			aucData,
			(acc, curr) => {
				acc[curr.trialTreatmentId] = { auc: curr.meanAuc, stdDev: curr.aucStdDev };
				return acc;
			},
			[],
		);

		return [body, auc, transposed];
	}, [aucData]);

	const treatmentMap = trialData
		? _.reduce<Treatment, string[]>(
				trialData.treatments,
				(acc, curr) => {
					acc[curr.trialTreatmentId] = curr.treatmentName ?? " - ";
					return acc;
				},
				[],
			)
		: [];

	const bodyColorMatrix = buildColorMatrix(tableBodyData) as {
		[flightId: string]: { opacity: string; textColor: string }[];
	};
	const aucColorMatrix = calculateShadingForNumbers(_.map(tableAucData, "auc"));

	const allTreatmentsChecked = !!pending.selectedTreatmentMatrix?.reduce(
		(acc, curr) => (curr !== undefined ? acc && curr : acc),
		true,
	);
	const someTreatmentsChecked =
		allTreatmentsChecked !== pending.selectedTreatmentMatrix?.reduce((acc, curr) => acc || curr, false);

	const allFlightsChecked = !!flights?.every((f) => pending.selectedFlightIds?.includes(f.flightId));
	const someFlightsChecked =
		allFlightsChecked !== !!flights?.some((f) => pending.selectedFlightIds?.includes(f.flightId));

	const decimalsPerFlight = useMemo(() => {
		return _.transform<Record<string, number[]>, Record<string, number>>(
			transposedBodyData,
			(acc, perTreatmentValues, flightId) => {
				const vals = [...perTreatmentValues];
				acc[flightId] = getDecimalsByGate(_.min(vals)!);

				return acc;
			},
			{},
		);
	}, [transposedBodyData]);

	const decimalsForAvgAuc = getDecimalsByGate(tableAucData.reduce((acc, curr) => Math.min(acc, curr.auc), Infinity));
	const decimalsForStdAuc = getDecimalsByGate(tableAucData.reduce((acc, curr) => Math.min(acc, curr.stdDev), Infinity));

	if (loadingTable) {
		return (
			<Segment basic style={{ width: "100%", overflow: "auto", padding: 0 }}>
				<Loader active />
			</Segment>
		);
	}

	if (flights) {
		return (
			<Segment basic style={{ width: "100%", overflow: "auto", padding: 0 }}>
				<Table collapsing selectable celled>
					<Table.Header>
						<Table.Row textAlign="center">
							<Table.HeaderCell className="stickyColumn" style={{ minWidth: 76 }}></Table.HeaderCell>
							<Table.HeaderCell className="stickyColumn" style={{ left: 76 }} rowSpan="1" colSpan="2">
								Treatment
							</Table.HeaderCell>
							<Table.HeaderCell
								rowSpan="1"
								colSpan={display.collapseTable ? pending.selectedFlightIds?.length : flights.length}
							>
								<div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
									<span>{flights.length > 0 ? analysisName : null} Timecourse</span>
									<div style={{ width: 1, height: "stretch", background: "var(--border)" }} />
									<Checkbox
										style={{ alignSelf: "flex-end" }}
										title={"Check All Flights"}
										label="Check All Flights"
										disabled={!isInitialized}
										checked={allFlightsChecked}
										indeterminate={someFlightsChecked}
										onChange={(_event, { checked }) => {
											if (checked) {
												changeSettings({
													selectedFlightIds: _.map(flights, (f) => f.flightId),
												});
											} else {
												changeSettings({
													selectedFlightIds: [],
												});
											}
										}}
									/>
								</div>
							</Table.HeaderCell>
							{committed.selectedFlightIds && committed.selectedFlightIds.length > 1 && (
								<Table.HeaderCell rowSpan="1" colSpan="2">
									Area Under the Curve
								</Table.HeaderCell>
							)}
						</Table.Row>
						<Table.Row textAlign="center">
							<Table.HeaderCell className="stickyColumn">
								<Checkbox
									title={"Check All Treatments"}
									label="Check All"
									disabled={!isInitialized}
									checked={allTreatmentsChecked}
									indeterminate={someTreatmentsChecked}
									onChange={(_event, { checked }) => {
										changeSettings({
											selectedTreatmentMatrix: _.reduce(
												aucData,
												(acc, curr) => {
													acc[curr.trialTreatmentId] = !!checked;
													return acc;
												},
												[] as boolean[],
											),
										});
									}}
								/>
							</Table.HeaderCell>
							<Table.HeaderCell className="stickyColumn" style={{ left: 76, minWidth: 44 }}>
								#
							</Table.HeaderCell>
							<Table.HeaderCell className="stickyColumn" style={{ left: 120, minWidth: 159 }}>
								Name
							</Table.HeaderCell>
							{_.map(flights, (f) => {
								const isFlightChecked = pending.selectedFlightIds?.includes(f.flightId);
								if (display.collapseTable && !isFlightChecked) return;

								return (
									<Table.HeaderCell textAlign="center" key={f.flightId}>
										<Checkbox
											checked={pending.selectedFlightIds?.includes(f.flightId)}
											label={DateTime.fromISO(f.flightDate).toLocal().toFormat(DATE_FORMAT)}
											onChange={(_event, { checked }) => {
												if (loadingStats) return;
												if (checked) {
													changeSettings({ selectedFlightIds: [...pending.selectedFlightIds!, f.flightId] });
												} else {
													changeSettings({
														selectedFlightIds: _.filter(pending.selectedFlightIds, (sf) => sf !== f.flightId),
													});
												}
											}}
											disabled={loadingStats}
										/>
										<br />
										{!f.isPlantFlight && weatherDataEnabled ? (
											gduList.length > 0 ? (
												"GDUs: " +
												gduList
													.slice(
														0,
														gduList.findIndex((item) =>
															DateTime.fromISO(item.day).hasSame(DateTime.fromISO(f.flightDate), "day"),
														),
													)
													.reduce((a, b) => a + b.gdu, 0)
													.toFixed(0)
											) : (
												"N/A"
											)
										) : gduList.length > 0 ? (
											<br />
										) : (
											""
										)}
									</Table.HeaderCell>
								);
							})}
							{committed.selectedFlightIds && committed.selectedFlightIds.length > 1 && (
								<>
									<Table.HeaderCell>AVG</Table.HeaderCell>
									<Table.HeaderCell>STDEV</Table.HeaderCell>
								</>
							)}
						</Table.Row>
					</Table.Header>
					<Table.Body>
						{_.map(tableBodyData, (perFlightValues, trialTreatmentId) => {
							if (!perFlightValues) return;
							const isTreatmentChecked = !!pending.selectedTreatmentMatrix?.[trialTreatmentId];
							if (display.collapseTable && !isTreatmentChecked) return;
							const aucValue = tableAucData[trialTreatmentId].auc;
							const aucStdDev = tableAucData[trialTreatmentId].stdDev;

							const aucLetter = aucMeanComparisons?.letteringPairs.every(
								(lp, _, source) => lp.letter === source[0].letter,
							)
								? " - "
								: aucMeanComparisons?.letteringPairs.find((lp) => lp.treatmentId === trialTreatmentId)?.letter;

							const aucOpacity = aucColorMatrix[trialTreatmentId].opacity;

							const treatmentName = trialData?.treatments.find(
								(trt) => trt.trialTreatmentId === trialTreatmentId,
							)?.treatmentName;

							return (
								<Table.Row key={"treatment-" + trialTreatmentId}>
									<Table.Cell className="stickyColumn" textAlign="center" collapsing>
										<Checkbox
											checked={isTreatmentChecked}
											disabled={loadingStats}
											onChange={(_event, { checked }) => {
												if (loadingStats) return;
												const newMatrix = pending.selectedTreatmentMatrix ? [...pending.selectedTreatmentMatrix] : [];
												newMatrix[trialTreatmentId] = !!checked;
												changeSettings({ selectedTreatmentMatrix: newMatrix });
											}}
										/>
									</Table.Cell>
									<Table.Cell className="stickyColumn" style={{ left: 76 }} textAlign="center" collapsing>
										{trialTreatmentId}
									</Table.Cell>
									<Table.Cell
										className="stickyColumn"
										style={{ left: 120 }}
										title={
											trialData && trialData.treatments && trialData.treatments.length > 0 ? treatmentName : undefined
										}
									>
										<div className="longText">{treatmentMap.length > 0 ? treatmentMap[trialTreatmentId] : null}</div>
									</Table.Cell>
									{_.map(flights, (flight) => {
										const isFlightChecked = pending.selectedFlightIds?.includes(flight.flightId);
										if (display.collapseTable && !isFlightChecked) return;
										const value = perFlightValues[flight.flightId];

										const flightLetterPairs = timecourseMeanComparisons?.find(
											(mc) => mc.flightId === flight.flightId,
										)?.treatments;

										const letter = flightLetterPairs?.every((lp) => lp.letter === "a")
											? " - "
											: flightLetterPairs?.find((lp) => lp.trialTreatmentId === trialTreatmentId)?.letter;

										const opacity = bodyColorMatrix[flight.flightId]?.[trialTreatmentId].opacity;
										const decimals = decimalsPerFlight[flight.flightId];

										return !isLettersOutdated ? (
											<Table.Cell
												key={flight.flightId + trialTreatmentId}
												textAlign="right"
												style={{
													color:
														isTreatmentChecked && isFlightChecked && opacity !== "0%"
															? `contrast-color(color-mix(in srgb, var(--data-positive) ${opacity}, transparent))`
															: "black",
													backgroundColor:
														isTreatmentChecked && isFlightChecked
															? `color-mix(in srgb, var(--data-positive) ${opacity}, transparent)`
															: "lightGray",
												}}
											>
												<Grid className="aucTableGrid" columns={loadingStats || letter ? 2 : 1}>
													<Grid.Row>
														<Grid.Column className="aucTableNumber">
															{flight.isPlantFlight || flight.isHarvestFlight
																? ""
																: value && isTreatmentChecked && isFlightChecked
																	? value.toFixed(decimals)
																	: ""}
														</Grid.Column>

														{(loadingStats || letter) && (
															<Grid.Column style={{ textAlign: "left", overflowWrap: "break-word" }}>
																{isLettersOutdated || !isTreatmentChecked || !isFlightChecked ? (
																	""
																) : letter && !loadingStats ? (
																	letter
																) : (
																	<Loader active size="tiny" />
																)}
															</Grid.Column>
														)}
													</Grid.Row>
												</Grid>
											</Table.Cell>
										) : (
											<Table.Cell
												key={flight.flightId + trialTreatmentId}
												textAlign="center"
												style={{
													backgroundColor:
														isTreatmentChecked && isFlightChecked
															? `color-mix(in srgb, var(--data-positive) ${opacity}, transparent)`
															: "lightGray",
												}}
											>
												<Grid className="aucTableGrid" columns={2}>
													<Grid.Row>
														<Grid.Column className="aucTableNumber">
															{flight.isPlantFlight || flight.isHarvestFlight
																? ""
																: value && isTreatmentChecked && isFlightChecked
																	? value.toFixed(decimals)
																	: ""}
														</Grid.Column>
													</Grid.Row>
												</Grid>
											</Table.Cell>
										);
									})}

									{committed.selectedFlightIds && committed.selectedFlightIds.length > 1 && (
										<>
											<Table.Cell
												textAlign="right"
												collapsing
												style={{
													backgroundColor:
														aucOpacity === "NaN%" && isTreatmentChecked
															? ""
															: isTreatmentChecked && !isAucOutdated
																? `color-mix(in srgb, var(--data-primary) ${aucOpacity}, transparent)`
																: isAucOutdated
																	? ""
																	: "lightGray",
												}}
											>
												<Grid className="aucTableGrid" columns={loadingStats || aucLetter ? 2 : 1}>
													<Grid.Row>
														<Grid.Column className="aucTableNumber">
															{isTreatmentChecked && !isAucOutdated ? aucValue.toFixed(decimalsForAvgAuc) : ""}
														</Grid.Column>

														{(loadingStats || aucLetter) && (
															<Grid.Column style={{ textAlign: "left", overflowWrap: "break-word" }}>
																{!loadingStats ? (
																	isTreatmentChecked && !isAucOutdated && !isLettersOutdated && aucLetter
																) : (
																	<Loader active size="tiny" />
																)}
															</Grid.Column>
														)}
													</Grid.Row>
												</Grid>
											</Table.Cell>
											<Table.Cell
												className="aucTableNumber"
												textAlign="center"
												collapsing
												style={{
													backgroundColor: isTreatmentChecked && !isAucOutdated ? "" : isAucOutdated ? "" : "lightGray",
												}}
											>
												{(isTreatmentChecked && isNaN(aucStdDev)) || !isFinite(aucStdDev)
													? 0
													: isTreatmentChecked && !isAucOutdated
														? aucStdDev.toFixed(decimalsForStdAuc)
														: ""}
											</Table.Cell>
										</>
									)}
								</Table.Row>
							);
						})}
						{!!statsData && (
							<>
								<Table.Row>
									<Table.Cell className="stickyColumn" colSpan={3}>
										Average CV
									</Table.Cell>
									{_.map(flights, ({ flightId }) => {
										const isFlightChecked = pending.selectedFlightIds?.includes(flightId);
										if (display.collapseTable && !isFlightChecked) return;
										const stats = statsData.find((sd) => sd.flightId === flightId);

										return (
											<Table.Cell
												key={"cv-" + flightId}
												style={
													!isFlightChecked
														? {
																backgroundColor: "lightGray",
															}
														: {}
												}
											>
												<Grid className="aucTableGrid" columns={timecourseMeanComparisons?.length ? 2 : 1}>
													<Grid.Row>
														<Grid.Column className="aucTableNumber">
															{stats && Number(stats.cv) !== 0 && isFlightChecked
																? !isLettersOutdated
																	? Number(stats.cv).toFixed(4)
																	: " - "
																: ""}
														</Grid.Column>
													</Grid.Row>
												</Grid>
											</Table.Cell>
										);
									})}
									{/* AUC */}
									{committed.selectedFlightIds && committed.selectedFlightIds.length > 1 && (
										<>
											<Table.Cell>
												<Grid
													className="aucTableGrid"
													style={{ width: "100%" }}
													columns={aucMeanComparisons?.letteringPairs.length ? 2 : 1}
												>
													<Grid.Row>
														<Grid.Column className="aucTableNumber">
															{!isAucOutdated
																? Number(aucMeanComparisons?.cv) !== 0 && Number(aucMeanComparisons?.cv).toFixed(4)
																: ""}
														</Grid.Column>
													</Grid.Row>
												</Grid>
											</Table.Cell>
											<Table.Cell />
										</>
									)}
								</Table.Row>
								<Table.Row>
									<Table.Cell className="stickyColumn" colSpan={3}>
										<div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
											<span>p-Value</span>
											{!!timecourseAnovaTableRows?.length && (
												<Popup
													trigger={<span className="popupTriggerText">ANOVA Table</span>}
													on={"click"}
													content={
														<div className="aucTableContainer" style={{ padding: 0 }}>
															<Table basic="very" celled>
																<Table.Header>
																	<Table.Row style={{ whiteSpace: "nowrap" }}>
																		<Table.HeaderCell
																			style={{ borderTop: "none", textAlign: "center" }}
																		></Table.HeaderCell>
																		<Table.HeaderCell style={{ borderTop: "none", textAlign: "center" }}>
																			DF
																		</Table.HeaderCell>
																		<Table.HeaderCell style={{ borderTop: "none", textAlign: "center" }}>
																			F-Value
																		</Table.HeaderCell>
																		<Table.HeaderCell style={{ borderTop: "none", textAlign: "center" }}>
																			p-Value
																		</Table.HeaderCell>
																	</Table.Row>
																</Table.Header>
																<Table.Body>
																	{_.map(timecourseAnovaTableRows, (row) => {
																		if (row.term === "(Intercept)") return;
																		return (
																			<>
																				<Table.Row key={"anova-row-" + row.term}>
																					<Table.Cell>
																						<strong>{row.term}</strong>
																					</Table.Cell>
																					<Table.Cell className="aucTableNumber" style={{ textAlign: "right" }}>
																						{row.denDF.toFixed(1)}
																					</Table.Cell>
																					<Table.Cell className="aucTableNumber" style={{ textAlign: "right" }}>
																						{row.fValue.toFixed(2)}
																					</Table.Cell>
																					<Table.Cell className="aucTableNumber" style={{ textAlign: "right" }}>
																						{row.pValue.toFixed(4) !== "0.00000" ? row.pValue.toFixed(4) : "<0.0001"}
																					</Table.Cell>
																				</Table.Row>
																			</>
																		);
																	})}
																</Table.Body>
															</Table>
														</div>
													}
													basic
												/>
											)}
										</div>
									</Table.Cell>
									{timecoursePValue ? (
										<Table.Cell colSpan={display.collapseTable ? pending.selectedFlightIds?.length : flights?.length}>
											<Grid className="aucTableGrid" columns={1} style={{ margin: "auto" }}>
												<Grid.Row>
													<Grid.Column className="aucTableNumber">
														{!isLettersOutdated
															? Number(timecoursePValue).toFixed(4) !== "0.0000"
																? Number(timecoursePValue).toFixed(4)
																: "<0.0001"
															: " - "}
													</Grid.Column>
												</Grid.Row>
											</Grid>
										</Table.Cell>
									) : (
										_.map(flights, ({ flightId }) => {
											const isFlightChecked = pending.selectedFlightIds?.includes(flightId);
											if (display.collapseTable && !isFlightChecked) return;
											const stats = statsData.find((sd) => sd.flightId === flightId);

											return (
												<Table.Cell
													key={"p-value-" + flightId}
													style={
														!isFlightChecked
															? {
																	backgroundColor: "lightGray",
																}
															: {}
													}
												>
													<Grid className="aucTableGrid" columns={timecourseMeanComparisons?.length ? 2 : 1}>
														<Grid.Row>
															<Grid.Column className="aucTableNumber">
																{stats && isFlightChecked
																	? !isLettersOutdated
																		? Number(stats.pValue).toFixed(4) !== `0.${"0".repeat(4)}`
																			? Number(stats.pValue).toFixed(4)
																			: `<0.${"0".repeat(4 - 1)}1`
																		: " - "
																	: ""}
															</Grid.Column>
														</Grid.Row>
													</Grid>
												</Table.Cell>
											);
										})
									)}

									{/* AUC */}
									<Table.Cell>
										<Grid
											className="aucTableGrid"
											style={{ width: "100%" }}
											columns={aucMeanComparisons?.letteringPairs.length ? 2 : 1}
										>
											<Grid.Row>
												<Grid.Column className="aucTableNumber">
													{!isAucOutdated
														? Number(aucMeanComparisons?.pValue).toFixed(4) !== `0.${"0".repeat(4)}`
															? Number(aucMeanComparisons?.pValue).toFixed(4)
															: `<0.${"0".repeat(4 - 1)}1`
														: ""}
												</Grid.Column>
											</Grid.Row>
										</Grid>
									</Table.Cell>
									<Table.Cell />
								</Table.Row>
							</>
						)}
					</Table.Body>
				</Table>
			</Segment>
		);
	}

	return <Loader active />;
};

export default AreaUnderTheCurveTableNew;
